import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  radioHealthData,
  mostActiveRadios,
  leastActiveRadios,
  streamDownDetailedRadios,
  pendingReviewRadios,
  healthKPIs,
  type RadioHealthData,
} from '@/mocks/radioHealth';
import { liveRadios, liveRadiosStats, type LiveRadio, type SortMode } from '@/mocks/liveRadios';
import { radioGrowthData, type RadioGrowthData } from '@/mocks/growthCenter';

type LoadingState = 'loading' | 'ready' | 'error';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatPercent(n: number): string {
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 500 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.96) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve(data);
    }, d);
  });
}

/* ── KPI Card ── */
function KpiCard({ label, value, icon, bg, color, subtitle }: {
  label: string; value: string; icon: string; bg: string; color: string; subtitle?: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
        {subtitle && <p className="text-foreground-400 text-[9px]">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ── Status Badge (Live) ── */
function StatusBadge({ status }: { status: LiveRadio['status'] }) {
  const config: Record<LiveRadio['status'], { label: string; color: string; icon: string }> = {
    'on-air': { label: 'Al Aire', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-broadcast-line' },
    'live-event': { label: 'Evento en Vivo', color: 'bg-rose-50 text-rose-700 border-rose-200', icon: 'ri-live-line' },
    'live-show': { label: 'Show en Vivo', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-star-line' },
    'special': { label: 'Especial', color: 'bg-violet-50 text-violet-700 border-violet-200', icon: 'ri-sparkling-line' },
  };
  const c = config[status];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${c.color} text-[9px] font-semibold`}><i className={`${c.icon} text-[9px]`}></i>{c.label}</span>;
}

/* ── Health Score Badge ── */
function HealthScoreBadge({ score }: { score: number }) {
  const color = score >= 85 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : score >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${color} text-[10px] font-bold`}>{score}/100</span>;
}

/* ── Growth Trend Badge ── */
function GrowthBadge({ pct }: { pct: number }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] font-bold ${pct >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
      <i className={`${pct >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[9px]`}></i>
      {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
}

type TabKey = 'performance' | 'health' | 'live' | 'growth';

export default function SuperAdminRadioAnalytics() {
  const [activeTab, setActiveTab] = useState<TabKey>('performance');
  const [loadState, setLoadState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Live radios state
  const [liveRadioList, setLiveRadioList] = useState<LiveRadio[]>([]);
  const [sortMode, setSortMode] = useState<SortMode>('listeners');
  const [viewLiveRadio, setViewLiveRadio] = useState<LiveRadio | null>(null);

  // Health state
  const [healthTab, setHealthTab] = useState<'todas' | 'activas' | 'menos_activas' | 'stream_caido' | 'pendientes'>('todas');

  // Growth state
  const [growthSearch, setGrowthSearch] = useState('');
  const [growthSort, setGrowthSort] = useState<'growth' | 'followers' | 'listeners'>('growth');

  const loadData = useCallback(() => {
    setLoadState('loading');
    setErrorMsg(null);
    simulateLoad({ liveRadios, radioHealthData, radioGrowthData })
      .then((data) => {
        setLiveRadioList(data.liveRadios);
        setLoadState('ready');
      })
      .catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Sorted live radios ── */
  const sortedLive = useMemo(() => {
    return [...liveRadioList].sort((a, b) => {
      if (sortMode === 'activity') return b.activityScore - a.activityScore;
      if (sortMode === 'interaction') return b.interactionScore - a.interactionScore;
      return b.currentListeners - a.currentListeners;
    });
  }, [liveRadioList, sortMode]);

  /* ── Live metrics ── */
  const liveMetrics = useMemo(() => ({
    totalRadios: sortedLive.length,
    totalListeners: sortedLive.reduce((s, r) => s + r.currentListeners, 0),
    totalPrograms: sortedLive.reduce((s, r) => s + r.programs.length, 0),
    totalBroadcasters: sortedLive.reduce((s, r) => s + r.broadcasters.length, 0),
    avgActivity: sortedLive.length > 0 ? Math.round(sortedLive.reduce((s, r) => s + r.activityScore, 0) / sortedLive.length) : 0,
    avgInteraction: sortedLive.length > 0 ? Math.round(sortedLive.reduce((s, r) => s + r.interactionScore, 0) / sortedLive.length) : 0,
  }), [sortedLive]);

  /* ── Health tab data ── */
  const healthTabData = useMemo(() => {
    switch (healthTab) {
      case 'activas': return mostActiveRadios;
      case 'menos_activas': return leastActiveRadios;
      case 'stream_caido': return streamDownDetailedRadios;
      case 'pendientes': return pendingReviewRadios;
      default: return radioHealthData;
    }
  }, [healthTab]);

  /* ── Sorted growth radios ── */
  const sortedGrowth = useMemo(() => {
    let result = [...radioGrowthData];
    if (growthSearch.trim()) {
      const q = growthSearch.toLowerCase();
      result = result.filter((r) => r.radioName.toLowerCase().includes(q) || r.radioCity.toLowerCase().includes(q) || r.radioFrequency.toLowerCase().includes(q));
    }
    if (growthSort === 'growth') result.sort((a, b) => b.growthPercent - a.growthPercent);
    else if (growthSort === 'followers') result.sort((a, b) => b.newFollowers - a.newFollowers);
    else result.sort((a, b) => b.newListeners - a.newListeners);
    return result;
  }, [growthSearch, growthSort]);

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'performance', label: 'Performance', icon: 'ri-dashboard-line' },
    { key: 'health', label: 'Health Monitoring', icon: 'ri-heart-pulse-line' },
    { key: 'live', label: 'Live Now', icon: 'ri-live-line' },
    { key: 'growth', label: 'Growth', icon: 'ri-line-chart-line' },
  ];

  const healthTabs = [
    { key: 'todas' as const, label: 'Todas', icon: 'ri-radio-line', count: radioHealthData.length },
    { key: 'activas' as const, label: 'Más Activas', icon: 'ri-star-line', count: mostActiveRadios.length },
    { key: 'menos_activas' as const, label: 'Menos Activas', icon: 'ri-arrow-down-circle-line', count: leastActiveRadios.length },
    { key: 'stream_caido' as const, label: 'Stream Caído', icon: 'ri-close-circle-line', count: streamDownDetailedRadios.length },
    { key: 'pendientes' as const, label: 'Pendientes', icon: 'ri-error-warning-line', count: pendingReviewRadios.length },
  ];

  const handleExport = async () => {
    const result = await SwalAlert.confirm('Exportar datos', '¿Exportar los datos visibles como CSV?');
    if (result.isConfirmed) SwalAlert.success('Exportado', 'Archivo generado exitosamente (mock).');
  };

  const renderLayout = (children: React.ReactNode) => (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Radio Analytics</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Radio Analytics</h2>
                <p className="text-foreground-400 text-xs mt-0.5">Performance · Health · Live Now · Growth</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-600 text-[10px] font-medium cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap">
                  <i className="ri-file-excel-2-line text-[10px]"></i>Exportar CSV
                </button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {tabs.map((tab) => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'}`}>
                  <i className={`${tab.icon} text-[11px]`}></i>{tab.label}
                </button>
              ))}
            </div>
            {children}
          </div>
        </main>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Radio Analytics</p>
      </div>
    </div>
  );

  if (loadState === 'loading') {
    return renderLayout(
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 rounded-xl bg-background-200"></div>
              <div className="space-y-1"><div className="w-16 h-2.5 bg-background-200 rounded"></div><div className="w-10 h-4 bg-background-200 rounded"></div></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return renderLayout(
      <div className="flex items-center justify-center py-12">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><i className="ri-error-warning-line text-red-500 text-2xl"></i></div>
          <h2 className="text-foreground-950 font-bold text-base mb-1.5">Error cargando datos</h2>
          <p className="text-foreground-400 text-xs mb-5">{errorMsg || 'No se pudieron cargar los datos.'}</p>
          <button onClick={loadData} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
        </div>
      </div>
    );
  }

  /* ─── PERFORMANCE TAB ─── */
  const performanceContent = (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KpiCard label="Oyentes Activos" value={formatNumber(liveMetrics.totalListeners)} icon="ri-headphone-line" bg="bg-primary-50" color="text-primary-500" />
        <KpiCard label="Radios Transmitiendo" value={String(liveMetrics.totalRadios)} icon="ri-radio-line" bg="bg-emerald-50" color="text-emerald-500" />
        <KpiCard label="Programas al Aire" value={String(liveMetrics.totalPrograms)} icon="ri-mic-line" bg="bg-amber-50" color="text-amber-500" />
        <KpiCard label="Locutores al Aire" value={String(liveMetrics.totalBroadcasters)} icon="ri-user-star-line" bg="bg-rose-50" color="text-rose-500" />
        <KpiCard label="Actividad Promedio" value={`${liveMetrics.avgActivity}/100`} icon="ri-flashlight-line" bg="bg-stone-50" color="text-stone-500" />
        <KpiCard label="Interacción Prom." value={`${liveMetrics.avgInteraction}/100`} icon="ri-chat-1-line" bg="bg-orange-50" color="text-orange-500" />
        <KpiCard label="Stream Caído" value={String(healthKPIs.streamCaido)} icon="ri-close-circle-line" bg="bg-red-50" color="text-red-500" />
        <KpiCard label="En Riesgo" value={String(healthKPIs.enRiesgo)} icon="ri-alert-line" bg="bg-amber-50" color="text-amber-500" />
      </div>
      <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><i className="ri-information-line text-accent-500 text-lg"></i></div>
          <div><p className="text-accent-800 text-xs font-semibold">Radio Performance</p><p className="text-accent-600/80 text-[10px]">Explorá los tabs Health, Live Now y Growth para análisis detallado.</p></div>
        </div>
      </div>
    </div>
  );

  /* ─── HEALTH TAB ─── */
  const healthContent = (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'Stream Caído', value: healthKPIs.streamCaido, icon: 'ri-close-circle-line', bg: 'bg-red-500' },
          { label: 'En Riesgo', value: healthKPIs.enRiesgo, icon: 'ri-alert-line', bg: 'bg-orange-500' },
          { label: 'Ocultas', value: healthKPIs.ocultasDirectorio, icon: 'ri-eye-off-line', bg: 'bg-amber-500' },
          { label: 'Suspendidas', value: healthKPIs.suspendidas, icon: 'ri-stop-circle-line', bg: 'bg-foreground-600' },
          { label: 'Pendientes', value: healthKPIs.pendientesRevision, icon: 'ri-error-warning-line', bg: 'bg-sky-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-background-50 rounded-2xl p-4 border border-background-200/70">
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className={`w-8 h-8 rounded-xl ${stat.bg} flex items-center justify-center`}><i className={`${stat.icon} text-white text-sm`}></i></div>
              <span className="text-foreground-500 text-[11px] font-semibold">{stat.label}</span>
            </div>
            <p className="text-foreground-950 text-2xl font-bold">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto">
        {healthTabs.map((tab) => (
          <button key={tab.key} onClick={() => setHealthTab(tab.key)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${healthTab === tab.key ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'}`}>
            <i className={`${tab.icon} text-[11px]`}></i>{tab.label}
            <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold ${healthTab === tab.key ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Radio</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Frecuencia</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Plan</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Estado</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Health Score</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Actividad</th>
              </tr>
            </thead>
            <tbody>
              {healthTabData.slice(0, 12).map((radio) => (
                <tr key={radio.radioId} className="border-b border-background-200 hover:bg-background-50 transition-colors cursor-pointer">
                  <td className="py-3 px-3"><p className="text-foreground-800 text-xs font-semibold">{radio.radioName || radio.name}</p></td>
                  <td className="py-3 px-3"><span className="text-foreground-400 text-[10px]">{radio.radioFrequency || radio.frequency}</span></td>
                  <td className="py-3 px-3"><span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">{radio.plan || 'N/A'}</span></td>
                  <td className="py-3 px-3">
                    {radio.streamStatus ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${radio.streamStatus === 'online' ? 'bg-emerald-50 text-emerald-700' : radio.streamStatus === 'inestable' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${radio.streamStatus === 'online' ? 'bg-emerald-400' : radio.streamStatus === 'inestable' ? 'bg-amber-400' : 'bg-red-400'}`}></span>
                        {radio.streamStatus === 'online' ? 'Online' : radio.streamStatus === 'inestable' ? 'Inestable' : radio.streamStatus === 'offline' ? 'Offline' : radio.streamStatus === 'error' ? 'Error' : 'Sin Respuesta'}
                      </span>
                    ) : (
                      <span className="text-foreground-400 text-[10px]">-</span>
                    )}
                  </td>
                  <td className="py-3 px-3"><HealthScoreBadge score={radio.healthScore || radio.activityScore || Math.floor(Math.random() * 40 + 40)} /></td>
                  <td className="py-3 px-3">
                    <div className="w-16 h-1.5 bg-background-200 rounded-full overflow-hidden">
                      <div className="h-full bg-primary-400 rounded-full" style={{ width: `${radio.healthScore || 50}%` }}></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-background-100 flex items-center justify-between text-foreground-400 text-[9px]">
          <span>{healthTabData.length} radios</span>
          <span>Health Score · Monitoreo continuo</span>
        </div>
      </div>
    </div>
  );

  /* ─── LIVE NOW TAB ─── */
  const liveContent = (
    <div className="space-y-5">
      <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><i className="ri-radio-line text-emerald-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Radios al Aire</p><p className="text-foreground-800 text-sm font-bold">{liveMetrics.totalRadios}</p></div></div>
        <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
        <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center"><i className="ri-user-voice-line text-primary-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Oyentes</p><p className="text-foreground-800 text-sm font-bold">{formatNumber(liveMetrics.totalListeners)}</p></div></div>
        <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
        <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><i className="ri-mic-line text-amber-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Programas</p><p className="text-foreground-800 text-sm font-bold">{liveMetrics.totalPrograms}</p></div></div>
        <div className="flex items-center gap-2 ml-auto"><div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div><span className="text-emerald-600 text-[10px] font-semibold">EN VIVO AHORA</span></div>
      </div>

      <div className="flex items-center gap-1.5 px-1 py-1 rounded-full bg-background-100 w-fit">
        {[{ key: 'listeners' as const, label: 'Por Oyentes', icon: 'ri-user-voice-line' }, { key: 'activity' as const, label: 'Por Actividad', icon: 'ri-flashlight-line' }, { key: 'interaction' as const, label: 'Por Interacción', icon: 'ri-chat-1-line' }].map((srt) => (
          <button key={srt.key} onClick={() => setSortMode(srt.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${sortMode === srt.key ? 'bg-foreground-950 text-background-50 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}>
            <i className={`${srt.icon} text-[11px]`}></i>{srt.label}
          </button>
        ))}
      </div>

      <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide w-10">#</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Radio</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Estado</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Programas</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Actividad</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Interacción</th>
                <th className="text-left py-3 px-3 text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">Oyentes</th>
              </tr>
            </thead>
            <tbody>
              {sortedLive.map((radio, idx) => (
                <tr key={radio.id} className="border-b border-background-200 hover:bg-background-50 transition-colors cursor-pointer" onClick={() => setViewLiveRadio(radio)}>
                  <td className="py-3 px-3"><span className="text-foreground-400 text-[10px] font-semibold">{idx + 1}</span></td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <img src={radio.thumbnailUrl} alt={radio.name} className="w-9 h-9 rounded-lg object-cover" />
                      <div>
                        <p className="text-foreground-800 text-xs font-bold line-clamp-1">{radio.name}</p>
                        <p className="text-foreground-400 text-[9px]">{radio.frequency}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3"><StatusBadge status={radio.status} /></td>
                  <td className="py-3 px-3"><span className="text-foreground-800 text-xs font-semibold">{radio.programs.length}</span></td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2 min-w-[80px]"><div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden"><div className="h-full bg-primary-500 rounded-full" style={{ width: `${radio.activityScore}%` }}></div></div><span className="text-foreground-800 text-xs font-bold">{radio.activityScore}</span></div>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2 min-w-[80px]"><div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${radio.interactionScore}%` }}></div></div><span className="text-foreground-800 text-xs font-bold">{radio.interactionScore}</span></div>
                  </td>
                  <td className="py-3 px-3"><span className="text-foreground-800 text-xs font-bold">{formatNumber(radio.currentListeners)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-background-100 flex items-center justify-between text-foreground-400 text-[9px]">
          <span>{sortedLive.length} radios al aire</span>
          <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>Actualizado en tiempo real</span>
        </div>
      </div>
    </div>
  );

  /* ─── GROWTH TAB ─── */
  const growthContent = (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input type="text" value={growthSearch} onChange={(e) => setGrowthSearch(e.target.value)} placeholder="Buscar radio..." className="w-full pl-8 pr-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs">
          <span className="text-foreground-400 text-[10px] whitespace-nowrap">Ordenar:</span>
          {[{ key: 'growth' as const, label: 'Crecimiento' }, { key: 'followers' as const, label: 'Seguidores' }, { key: 'listeners' as const, label: 'Oyentes' }].map((srt) => (
            <button key={srt.key} onClick={() => setGrowthSort(srt.key)} className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${growthSort === srt.key ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-700'}`}>{srt.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { label: 'Radios Rastreadas', value: String(sortedGrowth.length), icon: 'ri-radio-line', bg: 'bg-sky-50', color: 'text-sky-500' },
          { label: 'Seguidores Nuevos', value: formatNumber(sortedGrowth.reduce((s, r) => s + r.newFollowers, 0)), icon: 'ri-user-add-line', bg: 'bg-primary-50', color: 'text-primary-500' },
          { label: 'Oyentes Nuevos', value: formatNumber(sortedGrowth.reduce((s, r) => s + r.newListeners, 0)), icon: 'ri-headphone-line', bg: 'bg-emerald-50', color: 'text-emerald-500' },
          { label: 'Crecimiento Prom.', value: `${sortedGrowth.length > 0 ? Math.round(sortedGrowth.reduce((s, r) => s + r.growthPercent, 0) / sortedGrowth.length) : 0}%`, icon: 'ri-line-chart-line', bg: 'bg-amber-50', color: 'text-amber-500' },
          { label: 'Programas Nuevos', value: String(sortedGrowth.reduce((s, r) => s + r.newPrograms, 0)), icon: 'ri-calendar-check-line', bg: 'bg-violet-50', color: 'text-violet-500' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
            <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center shrink-0`}><i className={`${kpi.icon} ${kpi.color} text-lg`}></i></div>
            <div className="min-w-0"><p className="text-foreground-400 text-[10px] font-medium truncate">{kpi.label}</p><p className="text-foreground-800 text-lg font-bold leading-tight">{kpi.value}</p></div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sortedGrowth.map((radio) => (
          <div key={radio.id} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0"><i className="ri-radio-line text-foreground-500 text-sm"></i></div>
                <div className="min-w-0">
                  <p className="text-foreground-800 text-xs font-semibold line-clamp-1">{radio.radioName}</p>
                  <p className="text-foreground-400 text-[9px]">{radio.radioFrequency} · {radio.radioCity}</p>
                </div>
              </div>
              <GrowthBadge pct={radio.growthPercent} />
            </div>
            <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${radio.growthPercent >= 15 ? 'bg-emerald-500' : radio.growthPercent >= 5 ? 'bg-sky-500' : radio.growthPercent >= 0 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.max(4, Math.min(100, radio.growthPercent * 2 + 20))}%` }}></div>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {[
                { v: radio.newFollowers, l: 'Seg.', i: 'ri-user-add-line' },
                { v: radio.newListeners, l: 'Oyent.', i: 'ri-headphone-line' },
                { v: radio.newPrograms, l: 'Prog.', i: 'ri-calendar-check-line' },
                { v: radio.newHosts, l: 'Loc.', i: 'ri-mic-line' },
                { v: radio.newSponsors, l: 'Spon.', i: 'ri-hand-heart-line' },
              ].map((m) => (
                <div key={m.l} className="text-center bg-background-100 rounded-lg py-2 px-1">
                  <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(m.v)}</p>
                  <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5"><i className={`${m.i} text-[7px]`}></i>{m.l}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const tabContent = activeTab === 'performance' ? performanceContent : activeTab === 'health' ? healthContent : activeTab === 'live' ? liveContent : growthContent;

  return (
    <>
      {renderLayout(tabContent)}
      {viewLiveRadio && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewLiveRadio(null)}></div>
          <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-start justify-between gap-4 z-10">
              <div className="flex items-center gap-3">
                <img src={viewLiveRadio.thumbnailUrl} alt={viewLiveRadio.name} className="w-12 h-12 rounded-xl object-cover" />
                <div><StatusBadge status={viewLiveRadio.status} /><h3 className="text-foreground-950 font-bold text-sm mt-1">{viewLiveRadio.name}</h3><p className="text-foreground-400 text-[10px]">{viewLiveRadio.frequency} · {viewLiveRadio.category}</p></div>
              </div>
              <button onClick={() => setViewLiveRadio(null)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"><i className="ri-close-line text-foreground-500 text-sm"></i></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-primary-50 rounded-xl p-3 text-center"><p className="text-primary-600 text-[9px] font-semibold">Actividad</p><p className="text-primary-700 text-xl font-bold">{viewLiveRadio.activityScore}</p></div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center"><p className="text-emerald-600 text-[9px] font-semibold">Interacción</p><p className="text-emerald-700 text-xl font-bold">{viewLiveRadio.interactionScore}</p></div>
                <div className="bg-amber-50 rounded-xl p-3 text-center"><p className="text-amber-600 text-[9px] font-semibold">Oyentes</p><p className="text-amber-700 text-xl font-bold">{formatNumber(viewLiveRadio.currentListeners)}</p></div>
              </div>
              <div className="bg-background-100 rounded-xl p-4">
                <p className="text-foreground-500 text-[10px] font-semibold mb-2">Programas al Aire ({viewLiveRadio.programs.length})</p>
                {viewLiveRadio.programs.map((p) => <div key={p.id} className="flex items-center gap-2 py-1.5"><span className="text-foreground-800 text-xs">{p.name}</span><span className="text-foreground-400 text-[9px] ml-auto">{p.startTime}-{p.endTime}</span></div>)}
              </div>
              <div className="flex justify-end gap-2">
                <Link to={`/radio/${viewLiveRadio.frequency.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '')}`} className="px-4 py-2 rounded-xl bg-primary-500 text-background-50 text-xs font-bold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"><i className="ri-external-link-line text-[10px] mr-1"></i>Ir a la Radio</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}